import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { searchIndex } from './searchIndex';
import { useAuth } from '~/contexts/AuthContext';
import './MenuSearch.css';

export default function MenuSearch() {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [results, setResults] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const wrapperRef = useRef(null);
    const listRef = useRef(null);
    const navigate = useNavigate();
    
    const { admin } = useAuth() || { admin: null };
    const isSuperAdmin = (() => {
        if (!admin) return false;
        const roles = admin.Roles || admin.roles || [];
        const roleString = admin.Role || admin.role || '';
        return roleString === 'Super Admin' || (Array.isArray(roles) && roles.includes('Super Admin'));
    })();
    const userPermissions = admin?.Permissions || admin?.permissions || [];

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }
        
        const lowerQuery = query.toLowerCase();
        const filtered = searchIndex.filter(item => {
            const matchesQuery = item.text.toLowerCase().includes(lowerQuery) || 
                                 item.subtext.toLowerCase().includes(lowerQuery);
            if (!matchesQuery) return false;

            if (isSuperAdmin) return true;

            if (!item.require) return false; // If no explicit permission, only Super Admin can see it
            
            const requireArr = Array.isArray(item.require) ? item.require : [item.require];
            return requireArr.some(p => userPermissions.includes(p));
        });
        setResults(filtered.slice(0, 10)); // Limit to 10 results
        setFocusedIndex(-1);
    }, [query]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (to) => {
        navigate(to);
        setIsOpen(false);
        setQuery('');
        setFocusedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (!isOpen || results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (focusedIndex >= 0 && focusedIndex < results.length) {
                handleSelect(results[focusedIndex].to);
            } else if (results.length > 0) {
                handleSelect(results[0].to);
            }
        }
    };

    useEffect(() => {
        if (focusedIndex >= 0 && listRef.current) {
            const activeElement = listRef.current.children[focusedIndex];
            if (activeElement) {
                activeElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [focusedIndex]);

    return (
        <div className="menu-search-wrapper" ref={wrapperRef}>
            <div className="menu-search-input-container">
                <Search size={16} className="menu-search-icon" />
                <input 
                    type="text" 
                    className="menu-search-input" 
                    placeholder="Search module or tab..."
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                />
            </div>
            
            {isOpen && query.trim() && (
                <div className="menu-search-dropdown">
                    {results.length > 0 ? (
                        <ul className="menu-search-results" ref={listRef}>
                            {results.map((item, idx) => (
                                <li 
                                    key={idx} 
                                    className={`menu-search-item ${focusedIndex === idx ? 'focused' : ''}`} 
                                    onClick={() => handleSelect(item.to)}
                                    onMouseEnter={() => setFocusedIndex(idx)}
                                >
                                    <div className="menu-search-item-text">{item.text}</div>
                                    <div className="menu-search-item-subtext">{item.subtext}</div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <div className="menu-search-no-results">
                            No modules or tabs found.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
